import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { ApiError, guestApi } from './lib/api.js';
import { HouseProvider, useHouse } from './lib/store.js';
import { Ambient, Toast } from './components/ui.js';
import { TabBar } from './components/Chrome.js';
import { CodeEntry } from './screens/CodeEntry.js';
import { Home } from './screens/Home.js';
import { Rooms } from './screens/Rooms.js';
import { RoomScreen } from './screens/RoomScreen.js';
import { Install } from './screens/Install.js';
import { Info } from './screens/Info.js';
import { Timings } from './screens/Timings.js';
import { Offline } from './screens/Offline.js';
import { AdminApp } from './admin/AdminApp.js';
import { useOnline } from './lib/online.js';

type AuthState = 'checking' | 'in' | 'out' | 'offline';

function GuestShell(): ReactNode {
  const { lastError, dismissError } = useHouse();
  return (
    <>
      <Ambient />
      <div className="app">
        <div className="app-scroll">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/rooms" element={<Rooms />} />
            <Route path="/room/:roomId" element={<RoomScreen />} />
            <Route path="/info" element={<Info />} />
            <Route path="/timings" element={<Timings />} />
            <Route path="/install" element={<Install />} />
            <Route path="*" element={<Home />} />
          </Routes>
        </div>
        <TabBar />
      </div>
      {lastError && <Toast message={lastError} onDismiss={dismissError} />}
    </>
  );
}

function GuestApp(): ReactNode {
  const [auth, setAuth] = useState<AuthState>('checking');
  const online = useOnline();

  const check = useCallback(async () => {
    try {
      await guestApi.context();
      setAuth('in');
    } catch (err) {
      // Only a real 401 means "not signed in". A network failure must not throw
      // the guest back to the code entry screen — their session is still valid,
      // the phone just has no signal.
      if (err instanceof ApiError) setAuth(err.status === 401 ? 'out' : 'in');
      else setAuth('offline');
    }
  }, []);

  useEffect(() => {
    void check();
  }, [check]);

  // Retry automatically the moment the device comes back.
  useEffect(() => {
    if (online && auth === 'offline') void check();
  }, [online, auth, check]);

  if (auth === 'checking') {
    return (
      <>
        <Ambient />
        <div className="empty">Connecting…</div>
      </>
    );
  }

  if (auth === 'offline') {
    return (
      <Offline
        onRetry={() => {
          setAuth('checking');
          void check();
        }}
      />
    );
  }

  if (auth === 'out' && !online) {
    return <Offline onRetry={() => void check()} />;
  }

  if (auth === 'out') return <CodeEntry onSuccess={() => setAuth('in')} />;

  return (
    <HouseProvider onUnauthenticated={() => setAuth('out')}>
      <GuestShell />
    </HouseProvider>
  );
}

export function App(): ReactNode {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/admin/*" element={<AdminApp />} />
        <Route path="*" element={<GuestApp />} />
      </Routes>
    </BrowserRouter>
  );
}
